import React, { useEffect, useState } from 'react'
import { ExpandLess, ExpandMore } from '@mui/icons-material'
import {
  CircularProgress,
  Collapse,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import classNames from 'classnames'
import * as R from 'ramda'

import i18nUiComponents from '../../locales/i18n'
import {
  ClassesType,
  Constant,
  Nil,
  Question,
  QuestionAnswer,
  QuestionAnswerInput,
} from '../../types'
import Text from '../Text'
import PriorQuestionAnswers from './PriorQuestionAnswers'
import {
  getMultipleAnswerDisplayValue,
  getQuestionInputMap,
} from './questionUtils'

const useStyles = makeStyles(
  theme => ({
    root: {},
    inputsContainer: {
      marginLeft: theme.spacing(4),
    },
    radioLabel: {
      fontSize: '1.6rem',
      color: theme.colors.secondaryText,
      marginLeft: theme.spacing(0.5),
    },
    radio: {
      padding: theme.spacing(0.5),
    },
    labelRoot: {
      margin: 0,
    },
    progress: {
      marginLeft: theme.spacing(1.5),
    },
    expandIcon: {
      width: 16,
      height: 16,
    },
    text: {},
  }),
  { name: 'QuestionView' },
)

export type QuestionMode = 'read-only' | 'edit'

interface QuestionViewBaseProps {
  AnswerInputTypes: Constant[]
  className?: string
  classes?: ClassesType<typeof useStyles>
  disabled?: boolean
  isLoading?: boolean
  mode?: QuestionMode
  onAnswered?: (question: Question) => void
  question: Question | Nil
}

interface QuestionViewWithPriorAnswers extends QuestionViewBaseProps {
  allAnswersExpanded: boolean
  renderPriorAnswers: boolean
}

interface QuestionViewWithoutPriorAnswers extends QuestionViewBaseProps {
  allAnswersExpanded?: never
  renderPriorAnswers?: never
}

export type QuestionViewProps =
  | QuestionViewWithPriorAnswers
  | QuestionViewWithoutPriorAnswers

const QuestionView = ({
  AnswerInputTypes,
  allAnswersExpanded,
  classes: classesProp,
  className,
  disabled,
  isLoading,
  mode,
  onAnswered,
  question: questionProp,
  renderPriorAnswers = false,
}: QuestionViewProps) => {
  const classes = useStyles({ classes: classesProp })

  const [question, setQuestion] = useState<Question | Nil>(questionProp)
  const [showPriorAnswers, setShowPriorAnswers] = useState(false)

  const answers = question?.answers || []
  const isSingleAnswer = answers.length === 1

  const onAnswerChange = (
    oldAnswer: QuestionAnswer,
    newAnswer: QuestionAnswer,
  ) => {
    const oldAnswerIndex = answers.indexOf(oldAnswer)
    const restOfAnswers = answers.map(answer => ({
      ...answer,
      selected: false,
    }))
    const newAnswers = R.update(oldAnswerIndex, newAnswer, restOfAnswers)
    const newQuestion = { ...question, answers: newAnswers } as Question

    setQuestion(newQuestion)

    if (onAnswered) {
      onAnswered(newQuestion)
    }
  }

  const onRadioValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const oldAnswer = answers.find(({ id, text }) =>
      id ? id === event.target.value : text === event.target.value,
    ) as QuestionAnswer
    const newAnswer = { ...oldAnswer, selected: true }

    onAnswerChange(oldAnswer, newAnswer)
  }

  const onInputValueChange = (
    value: any,
    input: QuestionAnswerInput,
    oldAnswer: QuestionAnswer,
  ) => {
    const oldInputIndex = oldAnswer.inputs.indexOf(input)
    const newInput = { ...input, value }
    const newInputs = R.update(oldInputIndex, newInput, oldAnswer.inputs)
    const newAnswer = {
      ...oldAnswer,
      selected: R.isEmpty(value) ? true : Boolean(value),
      inputs: newInputs,
    }

    onAnswerChange(oldAnswer, newAnswer)
  }

  const getAnswerInput = (answer: QuestionAnswer) =>
    (answer.inputs || []).map(input => {
      const { getValue, Component } = getQuestionInputMap(
        input.inputTypeId,
        AnswerInputTypes,
      )
      return React.cloneElement(Component, {
        onChange: (data: any) =>
          onInputValueChange(getValue(data), input, answer),
        value: input.value || '',
        disabled,
      })
    })

  const selectedAnswer = (question?.answers || []).find(
    ({ selected }) => selected,
  )

  const handleShowPriorAnswers = () => {
    setShowPriorAnswers(!showPriorAnswers)
  }

  useEffect(() => {
    setQuestion(questionProp)
  }, [questionProp])

  useEffect(() => {
    if (renderPriorAnswers && R.is(Boolean, allAnswersExpanded)) {
      setShowPriorAnswers(allAnswersExpanded)
    }
  }, [allAnswersExpanded, renderPriorAnswers])

  return (
    <Grid
      container
      className={classNames(className, classes.root)}
      direction="column"
      flexShrink={0}
      id={`qst-${question?.id}`}
      wrap="nowrap"
    >
      <Grid
        container
        alignItems="center"
        justifyContent="space-between"
        wrap="nowrap"
      >
        <Grid container item alignItems="center" wrap="nowrap">
          <Typography className={classes.text} display="inline" variant="body2">
            {question?.questionText}
          </Typography>
          {isLoading && (
            <CircularProgress className={classes.progress} size={16} />
          )}
        </Grid>
        {renderPriorAnswers && (question?.priorAnswers?.length ?? 0) > 0 && (
          <Grid container item justifyContent="flex-end">
            <IconButton
              disableRipple
              aria-label={`show-prior-question-${question?.id}`}
              sx={{ p: 0 }}
              onClick={handleShowPriorAnswers}
            >
              <Text mr={0.5} variant="lowAccent3">
                {i18nUiComponents.t<string>('Common:PRIOR_ANSWERS_COUNT', {
                  length: question?.priorAnswers?.length ?? 0,
                })}
              </Text>
              {showPriorAnswers ? (
                <ExpandLess className={classes.expandIcon} />
              ) : (
                <ExpandMore className={classes.expandIcon} />
              )}
            </IconButton>
          </Grid>
        )}
      </Grid>
      {mode && mode === 'read-only' ? (
        <>
          <Grid container item>
            <Text>
              {question?.answers.map(({ inputs, selected, text }) =>
                getMultipleAnswerDisplayValue({
                  AnswerInputTypes,
                  inputs,
                  text: selected ? text : null,
                }),
              )}
            </Text>
          </Grid>
          {renderPriorAnswers && (
            <Grid container item>
              <Collapse in={showPriorAnswers}>
                <PriorQuestionAnswers
                  AnswerInputTypes={AnswerInputTypes}
                  priorAnswers={question?.priorAnswers ?? []}
                />
              </Collapse>
            </Grid>
          )}
        </>
      ) : (
        <>
          <Grid item>
            {isSingleAnswer ? (
              getAnswerInput(answers[0])
            ) : (
              <FormControl fullWidth component="fieldset">
                <RadioGroup
                  aria-label="preview-answers"
                  name="answers1"
                  value={selectedAnswer?.id || selectedAnswer?.text || ''}
                  onChange={onRadioValueChange}
                >
                  {answers.map(answer => {
                    const value = answer.id || answer.text
                    const isSelectedAnswer = answer.id
                      ? answer.id === selectedAnswer?.id
                      : answer.text === selectedAnswer?.text

                    return (
                      <React.Fragment key={value}>
                        <FormControlLabel
                          classes={{
                            root: classes.labelRoot,
                            label: classes.radioLabel,
                          }}
                          control={<Radio className={classes.radio} />}
                          disabled={disabled}
                          label={answer.text}
                          value={value}
                        />
                        {isSelectedAnswer && (
                          <Grid item className={classes.inputsContainer}>
                            {getAnswerInput(answer)}
                          </Grid>
                        )}
                      </React.Fragment>
                    )
                  })}
                </RadioGroup>
              </FormControl>
            )}
          </Grid>
          {renderPriorAnswers && (
            <Collapse in={showPriorAnswers}>
              <PriorQuestionAnswers
                AnswerInputTypes={AnswerInputTypes}
                priorAnswers={question?.priorAnswers ?? []}
              />
            </Collapse>
          )}
        </>
      )}
    </Grid>
  )
}

export default QuestionView
